import './WaitListButton.styles.scss';

import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { useDebounce } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { Permission } from '../../../../model/enumeration/Permission.enum';
import { WaitlistPlayerResponse } from '../../../../model/WaitlistPlayerResponse';
import authStore from '../../../../store/AuthStore';
import notificationStore from '../../../../store/NotificationStore';
import permissionStore from '../../../../store/PermissionStore';
import waitListStore from '../../../../store/WaitListStore';
import AvailableBotsForSeat from '../../../AvailableBotsForSit/AvailableBotsForSeat';
import { WaitListButtonProps } from './WaitListButton.types';

const WaitListButton: FC<WaitListButtonProps> = ({
  tableId,
  isTableFull,
  room,
  gameType,
  limit,
  union,
  fromLobbyTable,
  tablePlayerCount,
  waitlistCount,
  onWaitListPlayersUpdate,
}) => {
  const [isWaitlistVisible, setIsWaitlistVisible] = useState(false);
  const [isAvailableBotsForSitVisible, setIsAvailableBotsForSitVisible] =
    useState(false);

  const [waitlistPlayers, setWaitlistPlayers] = useState<
    WaitlistPlayerResponse[]
  >([]);

  const [filteredWaitlistPlayers, setFilteredWaitlistPlayers] = useState<
    WaitlistPlayerResponse[]
  >([]);

  const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce(
    '',
    300,
  );

  const hasShowUniqueIdsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.SHOW_UNIQUE_IDS),
    [authStore.authenticatedUser],
  );

  const fetchWaitListPlayers = async (): Promise<void> => {
    if (isWaitlistVisible) {
      const waitlistPlayers = await waitListStore.getWaitListPlayers(tableId);
      setWaitlistPlayers(waitlistPlayers);
      setFilteredWaitlistPlayers(waitlistPlayers);
      if (onWaitListPlayersUpdate) {
        onWaitListPlayersUpdate(tableId, waitlistPlayers.length);
      }
    }
    return Promise.resolve();
  };

  useEffect(() => {
    return () => {
      setWaitlistPlayers([]);
      setFilteredWaitlistPlayers([]);
      setSearchValue('');
    };
  }, [isWaitlistVisible]);

  useEffect(() => {
    if (waitlistCount !== waitlistPlayers.length || waitlistCount === 0) {
      fetchWaitListPlayers().then();
    }
  }, [isWaitlistVisible, waitlistCount]);

  useEffect(() => {
    const value = debouncedSearchValue.toLowerCase();
    setFilteredWaitlistPlayers(
      waitlistPlayers.filter(
        bot =>
          bot.pid.toLowerCase().includes(value) ||
          bot.nick.toLowerCase().includes(value),
      ),
    );
  }, [debouncedSearchValue]);

  const onWaitlistButtonClick = (e: SyntheticEvent): void => {
    e.stopPropagation();
    setIsWaitlistVisible(true);
  };

  const header = (
    <div className="waitlist-header">
      <div className="text-center">WAITLIST</div>
      <div className="d-flex justify-content-between mt-4 position-relative">
        <IconField iconPosition="left">
          <InputIcon size={40} className="pi pi-search" />
          <InputText
            v-model="value1"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder="Search by Pid or Nick"
          />
        </IconField>
        <Button
          className="join-bots-btn"
          label="Join bots"
          severity="success"
          onClick={() => setIsAvailableBotsForSitVisible(true)}
        />
      </div>
    </div>
  );

  const onRemoveBotFromWaitList = (bot: WaitlistPlayerResponse): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to remove this bot (<b>{bot.pid}</b>) <br />{' '}
          from waitlist?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        waitListStore
          .removeBotFromWaitList(tableId, gameType, bot.pid)
          .then(() =>
            notificationStore.showNotification({
              severity: 'success',
              summary: 'Success',
              detail: (
                <>
                  Request sent to the bot (<b>{bot.pid}</b>) to leave the table
                  waitlist (<b>{tableId}</b>)
                </>
              ),
            }),
          )
          .catch(response => {
            notificationStore.showNotification({
              severity: 'error',
              summary: response.response.data.error || 'Error',
              detail: 'Error while sending request to the bot',
            });
          })
          .finally(() => fetchWaitListPlayers());
      },
    });
  };

  const onAvailableBotsForSitHide = (): void => {
    fetchWaitListPlayers().then(() => setIsAvailableBotsForSitVisible(false));
  };

  return (
    <>
      <Button
        className="WaitListButton-Component"
        icon="pi pi-calendar-clock"
        tooltip="Waitlist"
        severity="warning"
        rounded
        text
        onClick={onWaitlistButtonClick}
        badge={waitlistCount ? waitlistCount.toString() : undefined}
        badgeClassName="p-badge-warning m-0"
      />
      <Dialog
        onClick={e => e.stopPropagation()}
        visible={isWaitlistVisible}
        onHide={() => setIsWaitlistVisible(false)}
        header={header}
        className="WaitListButtonDialog-Component"
        data-ignore-outside-click
        maskClassName="ignore-outside-click"
      >
        {filteredWaitlistPlayers.map((player, index) => (
          <Card
            className="mb-3 bots-wrapper"
            pt={{
              content: {
                className:
                  'row justify-content-between align-items-center w-100',
              },
            }}
            header={
              player.myBot && (
                <Button
                  className="remove-btn"
                  icon="pi pi-times"
                  rounded
                  text
                  severity="danger"
                  aria-label="Close"
                  onClick={() => onRemoveBotFromWaitList(player)}
                />
              )
            }
            key={player.pid + index}
          >
            <Avatar
              className={`col-2 justify-content-center ${
                player.myBot ? 'bot-avatar' : 'player-avatar'
              } pe-0`}
              shape="circle"
              size="xlarge"
              image={player.myBot ? '/assets/bot.svg' : '/assets/player.svg'}
            />
            <div className={`${player.myBot ? 'col-8' : 'col-10'} ps-4`}>
              <div className="info-field">
                <b>Pid:</b>
                {` ${player.pid}`}
              </div>
              <div className="info-field">
                <b>Nick:</b>
                {` ${player.nick}`}
              </div>
              {hasShowUniqueIdsPermission && player.myBot && (
                <div className="info-field">
                  <b>Unique ID:</b>
                  {` ${player.uniqueId}`}
                </div>
              )}
            </div>
            {player.myBot && (
              <div className="col-2 opened-tables">
                {player.openedTables}/{player.openTableLimit}
              </div>
            )}
          </Card>
        ))}
      </Dialog>
      <AvailableBotsForSeat
        fromLobbyTable={fromLobbyTable}
        tableId={tableId}
        room={room}
        gameType={gameType}
        limit={limit}
        union={union}
        isVisible={isAvailableBotsForSitVisible}
        isTableFull={isTableFull}
        onHide={onAvailableBotsForSitHide}
        tablePlayerCount={tablePlayerCount}
      />
    </>
  );
};

export default WaitListButton;
